import React from 'react';
import type { ButtonProps } from '@/ui-components/atoms/Button/Button';
import type { BuildPlan, OfferChannels, OfferKey, PlanCardContent, ValidKey } from './PlanCard.types';

/**
 * Hrefs for every offer combination, already resolved through `useInheritParams` by the caller.
 * The resolver stays pure, so it cannot call hooks itself.
 */
export interface PlanCardInheritedOfferHrefs {
    base: string;
    allSelected: string;
    offer1: string;
    offer2: string;
    offer3: string;
    offer12: string;
    offer13: string;
    offer23: string;
}

export interface ResolvePlanCardOfferSelectionArguments {
    switches: Record<string, boolean>;
    planCardContent?: PlanCardContent;
    allAccessPriceText?: React.ReactNode;
    previousCta: ButtonProps | null;
    inheritedHrefs: PlanCardInheritedOfferHrefs;
}

export interface PlanCardOfferSelection {
    selectionKey: ValidKey;
    priceText: React.ReactNode;
    cta: ButtonProps | null;
    offerChannels: OfferChannels | null;
}

export const withInheritedHref = (cta?: ButtonProps | null, inheritedHref?: string): ButtonProps | null => {
    if (!cta) return null;

    return {
        ...cta,
        href: inheritedHref || cta.href,
    };
};

/**
 * Maps the selected switches onto the content key that holds that combination's price, CTA, and
 * channels. Selections are sorted, so toggle order does not affect the result.
 */
export const getPlanCardOfferCombinationKey = (switches: Record<string, boolean>): ValidKey => {
    const selectedNumbers = Object.entries(switches)
        .filter(([, isSelected]) => isSelected)
        .map(([key]) => key.replace('offer', ''))
        .sort();

    if (selectedNumbers.length === 0) return 'noneSelectedContent';
    if (selectedNumbers.length === 3) return 'allSelectedContent';
    return `offer${selectedNumbers.join('')}` as OfferKey;
};

export function resolvePlanCardOfferSelection({
    switches,
    planCardContent,
    allAccessPriceText,
    previousCta,
    inheritedHrefs,
}: ResolvePlanCardOfferSelectionArguments): PlanCardOfferSelection {
    const selectionKey = getPlanCardOfferCombinationKey(switches);

    if (selectionKey === 'noneSelectedContent') {
        return {
            selectionKey,
            priceText: planCardContent?.planPriceText,
            cta: withInheritedHref(planCardContent?.cta, inheritedHrefs.base),
            offerChannels: planCardContent?.planCardChannels ?? null,
        };
    }

    if (selectionKey === 'allSelectedContent') {
        return {
            selectionKey,
            priceText: allAccessPriceText,
            // Spreading the previous CTA means all-access inherits `color` (and so the card border)
            // from whichever offer CTA was on screen before the third switch, not from the base CTA.
            // Preserved deliberately.
            cta: {
                ...previousCta,
                href: inheritedHrefs.allSelected,
                children: <>Get All Access</>,
                ariaLabel: 'Get All Access',
                type: 'button',
                variant: previousCta?.variant || 'primary',
            },
            offerChannels: planCardContent?.planCardChannels ?? null,
        };
    }

    const buildPlanFields = (planCardContent?.buildPlan as BuildPlan) || ({} as BuildPlan);
    const inheritedOfferHrefs: Record<OfferKey, string> = {
        offer1: inheritedHrefs.offer1,
        offer2: inheritedHrefs.offer2,
        offer3: inheritedHrefs.offer3,
        offer12: inheritedHrefs.offer12,
        offer13: inheritedHrefs.offer13,
        offer23: inheritedHrefs.offer23,
    };

    return {
        selectionKey,
        priceText: (buildPlanFields[`${selectionKey}PriceText`] as React.ReactNode) || null,
        cta: withInheritedHref(
            (buildPlanFields[`${selectionKey}Cta`] as ButtonProps | null) || null,
            inheritedOfferHrefs[selectionKey],
        ),
        offerChannels: (buildPlanFields[`${selectionKey}Channels`] as OfferChannels | null) || null,
    };
}
